/**
 * `<ResultCard>` — one search result row (frontend.md §2.1; UX-reconciliation rip-and-replace).
 *
 * Rebuilt VERBATIM from `docs/refactor/_extracted-standalone-source/05_2cdfc807.jsx`
 * lines 312–340 (the standalone's `ResultCard`): a `.res-card` wrapper with
 *  - `.res-head` — `.res-title` + `.res-score` (with `.hi` variant at ≥0.9) + a
 *    `.chip` for the source/type;
 *  - `.res-snippet` — the highlighted excerpt, rendered as TEXT, not innerHTML
 *    (the standalone uses `dangerouslySetInnerHTML`; we don't — see XSS note below);
 *  - `.res-foot` — folder glyph + notebook · date · matched-chunk count, then `.tag`
 *    pills for the note's tags.
 *
 * Density modes (orthogonal to the standalone, kept from F4.2):
 *   - `cards` (default) — full layout: head + snippet + foot.
 *   - `compact` — single line: title · score · source · date · "Open" — for scanning.
 *
 * 2026-05-18 — clicking the CARD now navigates to the editor (with highlight
 * prime), not the drawer. The previous behaviour (card click → drawer) was a
 * UX trap: the natural action did nothing of what the user expected
 * (navigate, pulse, scroll, auto-expand folder), and only the small "Open"
 * button in the footer wired the cross-surface flow. The drawer is now a
 * SECONDARY action behind a peek-icon button in the footer.
 *
 * SECURITY: the snippet is rendered as text (not innerHTML). The backend's highlighted
 * excerpt format is a separate concern; rendering with `dangerouslySetInnerHTML` would
 * open an XSS surface (the boundary test `test_result_card_does_not_use_dangerouslysetinnerhtml`
 * enforces this). HTML rendering needs DOMPurify or a typed safe-html shape from the
 * backend. Tracked as F4.2.x docket.
 *
 * This is a child of `<VirtualizedResultList>` — it must render at a roughly stable
 * height for the virtualizer's size estimate to hold (different estimates per density).
 */

import { useState } from 'react';
import type { ResultItem } from '@/types';
import { Icon, AutoroutedBadge } from '@/components';
import { useNavigateToNote } from '@/hooks';
import {
  InlineTokenModal,
  type InlineTokenKind,
} from '@/editor/InlineTokenModal';
import type { ResultDensity } from './searchUrlState';
import { stripMarkdownSyntax } from './snippetText';

interface ResultCardProps {
  item: ResultItem;
  /** Open the detail drawer for this note. */
  onOpenDetail: (item: ResultItem) => void;
  /** Visual density — defaults to `cards`. */
  density?: ResultDensity;
}

export function ResultCard({
  item,
  onOpenDetail,
  density = 'cards',
}: ResultCardProps): JSX.Element {
  const goToNote = useNavigateToNote();
  // 2026-05-18 — modal state for chip clicks. Same InlineTokenModal as
  // the editor's hashtag-decorator click → "Explore this tag" flow.
  const [tokenModal, setTokenModal] = useState<{
    kind: InlineTokenKind;
    label: string;
    entityId?: string;
  } | null>(null);

  // The primary "open the note in the editor with the snippet highlighted"
  // action. Bound to the card body click + Enter key + the explicit footer
  // button so all three natural affordances do the same thing.
  const openInEditor = (): void => {
    const quoted = item.snippet ?? '';
    // eslint-disable-next-line no-console
    console.log('[hl] ResultCard.openInEditor', {
      noteId: item.noteId,
      title: item.title,
      snippetLen: quoted.length,
      snippetPreview: quoted.slice(0, 120),
    });
    goToNote({ noteId: item.noteId, quotedText: quoted });
  };

  // Secondary action — peek at the note in a side drawer without leaving
  // the search surface. Stops propagation so the card-body click handler
  // doesn't ALSO fire (which would navigate away).
  const openDrawer = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onOpenDetail(item);
  };

  // Standalone class set: `.res-card` + density modifier + score `.hi` at high confidence.
  const cardClass =
    'res-card result-card' + (density === 'compact' ? ' res-card--compact result-card--compact' : '');
  const scoreClass =
    'res-score result-card__score' +
    (item.score >= 0.9 ? ' hi result-card__score--hi' : '');
  const dateStr = new Date(item.updatedAt).toLocaleDateString();

  return (
    <article
      className={cardClass}
      onClick={openInEditor}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') openInEditor();
      }}
    >
      <div className="res-head">
        <div className="res-title">{item.title}</div>
        <AutoroutedBadge noteId={item.noteId} />
        <span className={scoreClass} title="Hybrid retrieval score">
          {item.score.toFixed(2)}
        </span>
        <span className="chip result-card__source">{item.source}</span>
      </div>

      {/* U7 (design review 2026-07-05 P1 item 2): the wire snippet is plain
          text carrying RAW markdown (semantic hits = raw chunk text; lexical
          ts_headline is configured with StartSel=''/StopSel='' — no <mark>
          spans arrive). Strip the syntax at render time only — the RAW
          snippet still feeds `openInEditor`'s quotedText handoff, whose
          matcher does its own normalization. */}
      {density === 'cards' && (
        <p className="res-snippet">{stripMarkdownSyntax(item.snippet ?? '')}</p>
      )}

      {/* 2026-05-18 — defensive `?? []` because the backend wire shape was
          extended in the same release; an older backend that hasn't been
          rolled yet returns ResultItem WITHOUT topTags / topEntities, and
          accessing `.length` on undefined throws. The check renders the
          chip row only when at least one chip exists (so old-backend
          results just show no chip row, no crash). */}
      {density === 'cards' &&
        ((item.topTags?.length ?? 0) > 0 || (item.topEntities?.length ?? 0) > 0) && (
          <div className="res-chips">
            {(item.topTags ?? []).slice(0, 5).map((tag) => (
              <button
                key={`tag:${tag.name}`}
                type="button"
                className="hashtag-chip"
                onClick={(e) => {
                  e.stopPropagation();
                  setTokenModal({ kind: 'hashtag', label: tag.name });
                }}
                title={`Explore tag ${tag.name}`}
              >
                #{tag.name}
              </button>
            ))}
            {/* U7 chip discipline (design review P2 item 5): neutral chip with
                the sidecar's two-letter type prefix (PE/OR/DA…) — colors come
                from searchSurface.css, never the accent orange. */}
            {(item.topEntities ?? []).slice(0, 5).map((ent) => (
              <button
                key={`ent:${ent.entityId}`}
                type="button"
                className="entity-chip"
                onClick={(e) => {
                  e.stopPropagation();
                  setTokenModal({
                    kind: 'entity',
                    label: ent.name,
                    entityId: ent.entityId,
                  });
                }}
                title={`Explore entity ${ent.name} (${ent.type})`}
              >
                {ent.type ? (
                  <span className="entity-chip__type" aria-hidden="true">
                    {ent.type.slice(0, 2).toUpperCase()}
                  </span>
                ) : null}
                {ent.name}
              </button>
            ))}
          </div>
        )}

      <div className="res-foot">
        <Icon name="calendar" size={12} />
        <span>{dateStr}</span>
        {item.matchedChunkIds.length > 0 && density === 'cards' && (
          <>
            <span aria-hidden="true">·</span>
            <span>{item.matchedChunkIds.length} matched chunks</span>
          </>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          onClick={openDrawer}
          title="Preview without leaving search"
          aria-label="Preview"
        >
          Preview
        </button>
      </div>
      {tokenModal !== null && (
        <InlineTokenModal
          kind={tokenModal.kind}
          label={tokenModal.label}
          entityId={tokenModal.entityId}
          onClose={() => setTokenModal(null)}
        />
      )}
    </article>
  );
}
